/**
 * `isMobileDevice` attempts to determine whether
 * a user's device is a mobile phone or not.
 *
 * There is not a reliable way to determine a user's device
 * with a single property from the browser APIs available,
 * so a mix of properties are checked to try and determine
 * if a device is mobile.
 *
 * The logic for `hasTouchScreen` was take from [MDN](https://developer.mozilla.org/en-US/docs/Web/HTTP/Browser_detection_using_the_user_agent#mobile_device_detection).
 *
 * @returns boolean
 */
export function isMobileDevice() {
  if (typeof window === 'undefined' || !navigator?.userAgent) {
    return false;
  }

  return (
    /\b(blackberry|webos|iphone|iemobile)\b/i.test(navigator.userAgent) ||
    /\b(android|windows phone)\b/i.test(navigator.userAgent) ||
    /\b(ipad|tablet)\b/i.test(navigator.userAgent)
  );
}

/**
 * `isLandscapeOrientation` is used to check whether a user's
 * device is in the 'landscape' orientation.
 *
 * @returns screen orientation
 */
export function isLandscapeOrientation(value: OrientationType) {
  return value === 'landscape-primary' || value === 'landscape-secondary';
}

/**
 * `isBrowser` is used to check whether a user is visiting the app through one of the recommended browsers (Chrome or Safari).
 *
 * @returns bool, true if one of the parsed browsers is used
 */
export const isRecommendedBrowser = () => {
  const userAgent = navigator?.userAgent;

  if (typeof window === 'undefined' || !userAgent) {
    return false;
  }

  const isEdge = /Edg/.test(userAgent);
  const isChrome = /chrome/i.test(userAgent) && !isEdge;
  const isSafari = /safari/i.test(userAgent) && !isEdge;

  return isChrome || isSafari;
};
